// Pull a human-readable message out of an axios-style error. The backend variously
// returns: a plain string body (controllers using `BadRequest(result.Error)`), an
// object with `.message` / `.error` / `.title` (custom errors and ProblemDetails),
// or ASP.NET ValidationProblemDetails with field errors nested under `.errors`.
// Falls back to the axios transport message (`Request failed with status code N`,
// `Network Error`, `canceled`, etc.) and finally to the caller-supplied default,
// so a 500 with an empty body still surfaces "status code 500" instead of a
// generic fallback that hides which kind of failure it was.
export function extractApiError(err: unknown, fallback: string): string {
    if (err && typeof err === "object") {
        const e = err as {
            response?: { data?: unknown };
            message?: unknown;
        };
        const data = e.response?.data;
        if (typeof data === "string" && data.trim().length > 0) {
            return data;
        }
        if (data && typeof data === "object") {
            const d = data as {
                message?: unknown;
                error?: unknown;
                title?: unknown;
                errors?: unknown;
            };
            // ValidationProblemDetails: `errors` is an object whose values are
            // string[] (or strings). Flatten to "field: msg1, msg2" so users see
            // which field failed instead of just the generic title.
            if (d.errors && typeof d.errors === "object") {
                const parts: string[] = [];
                for (const [field, value] of Object.entries(d.errors as Record<string, unknown>)) {
                    if (Array.isArray(value)) {
                        const msgs = value.filter((m): m is string => typeof m === "string");
                        if (msgs.length > 0) parts.push(`${field}: ${msgs.join(", ")}`);
                    } else if (typeof value === "string" && value.length > 0) {
                        parts.push(`${field}: ${value}`);
                    }
                }
                if (parts.length > 0) return parts.join("; ");
            }
            if (typeof d.message === "string" && d.message.length > 0) return d.message;
            if (typeof d.error === "string" && d.error.length > 0) return d.error;
            if (typeof d.title === "string" && d.title.length > 0) return d.title;
        }
        if (typeof e.message === "string" && e.message.length > 0) {
            return e.message;
        }
    }
    return fallback;
}
